import React, { createContext, useCallback, useContext, useLayoutEffect, useRef, useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@digital-twins/design-system/components/ui/tooltip'

export interface TextClampProps {
  children: React.ReactNode
  minLines?: number
  maxLines?: number
  lineHeight?: number | string
  descenderBuffer?: number | string
  className?: string
  style?: React.CSSProperties
  as?: keyof JSX.IntrinsicElements
  innerRef?: React.Ref<HTMLElement>
  id?: string
  title?: string
  'data-testid'?: string
  /**
   * Opt-in (D3, docs/plans/deck-matrix-truncation-tooltip.md): when the clamped content
   * overflows its line box, expose the full text via a fast per-instance Radix tooltip
   * (300ms delay) instead of relying on the browser's native `title` attribute (~2s,
   * unstylable, unconfigurable delay). Purely additive: omitting it keeps the existing
   * native-`title` behavior unchanged for all current callers.
   *
   * When enabled, the native `title` attribute is no longer forwarded (avoids a double
   * tooltip), and truncation is detected live via {@link isTextClampTruncated}.
   */
  tooltipOnTruncate?: boolean
  /**
   * D3 (docs/plans/2026-09-21-qual-editorial-scope-themes-plan.md): opt-in whole-line
   * clamp. Measures the flex-allotted height, then sets `-webkit-line-clamp` (and a
   * matching `maxHeight`) to a whole number of lines so `overflow: hidden` never cuts
   * a glyph mid-letter. Default off: existing callers stay byte-identical.
   *
   * Own effect, not gated by {@link tooltipOnTruncate} or `renderMode="static"`, so
   * the PDF pass gets the same whole-line box as the interactive slide.
   */
  fitLines?: boolean
  /**
   * D17 (docs/plans/2026-09-17-positioning-map-writing-and-render-plan.md): the render
   * context this clamp is painted in. `static` is the headless-browser PDF pass, where no
   * pointer and no focus ever arrive, so a Radix tooltip is a trigger that can never open
   * and a PDF carrying one advertises text the reader cannot reach.
   *
   * In `static` the {@link tooltipOnTruncate} path therefore renders no Radix trigger at
   * all and falls back to the native `title` attribute, which is the only carrier a
   * printed page can hold.
   *
   * Threaded explicitly rather than through a React context: `TextClamp` is a leaf used by
   * every slide type, and a context would make its behavior depend on an ancestor that
   * most of those call sites do not have. Optional and defaulted to `interactive`, so a
   * caller that does not pass it keeps byte-identical behavior.
   */
  renderMode?: 'interactive' | 'static'
  /**
   * Plots plan LB (owner decision 24): told whether the clamp cuts its text, so a
   * chart label can show its whole value beside it (`ChartLabel`). Enables the same
   * live detection as {@link tooltipOnTruncate} without its Radix wrapper, so the
   * clamped node is never remounted. Never called in a `static` render.
   */
  onTruncatedChange?: (truncated: boolean) => void
}

/**
 * The render context a whole page is painted in, set once by the page that knows it
 * (the static deck render, `InternalDeckRenderPage`), so every clamp on a slide follows
 * it without each call site threading `renderMode`. An explicit `renderMode` prop still
 * wins; with neither, a clamp is interactive.
 */
export const TextRenderModeContext = createContext<'interactive' | 'static' | undefined>(undefined)

function calcLineHeightValue(lineHeight?: number | string) {
  if (lineHeight == null) return '1.2'
  return typeof lineHeight === 'number' ? `${lineHeight}` : lineHeight
}

function isUnitlessLineHeightValue(lineHeight: string) {
  return /^-?\d*\.?\d+$/.test(lineHeight.trim())
}

function buildClampedHeight(
  lineHeight: string,
  lineCount: number,
  descenderBuffer?: string,
) {
  const trimmedLineHeight = lineHeight.trim()
  const lineBoxHeight = isUnitlessLineHeightValue(trimmedLineHeight)
    ? `${trimmedLineHeight} * ${lineCount} * 1em`
    : `${lineCount} * ${trimmedLineHeight}`

  if (descenderBuffer) {
    return `calc(${lineBoxHeight} + ${descenderBuffer})`
  }

  return `calc(${lineBoxHeight})`
}

/**
 * D4: extra px allowance before flagging truncation. `TextClamp` reserves a descender
 * buffer via `paddingBottom` + an equal negative `marginBottom` (see `descenderBuffer`
 * below), which can nudge `scrollHeight` a hair past `clientHeight` even when the text
 * fits fully. This tolerance absorbs that plus ordinary sub-pixel rounding.
 */
const DEFAULT_TRUNCATION_TOLERANCE_PX = 2

export interface TextClampTruncationOptions {
  /** Mirrors the `TextClamp` `maxLines` prop so detection picks the right axis. */
  maxLines?: number
  /** Override the default tolerance (px). See {@link DEFAULT_TRUNCATION_TOLERANCE_PX}. */
  tolerancePx?: number
}

/**
 * D4: isolated, unit-testable truncation check.
 *
 * - `maxLines === 1` clamps render as `nowrap` + ellipsis, so overflow shows up on the
 *   horizontal axis (`scrollWidth` vs `clientWidth`).
 * - Multi-line clamps (`-webkit-line-clamp`) overflow vertically, and the box they are
 *   measured against may be pinned by the clamp's `max-height` OR by a `flex-shrink`
 *   constraint, so both are lifted between the two reads.
 *
 * jsdom always reports `0` for these scroll metrics, so this function only produces a
 * real answer in a real layout engine; tests stub `HTMLElement.prototype` to exercise it.
 */
export function isTextClampTruncated(
  el: HTMLElement | null | undefined,
  options: TextClampTruncationOptions = {},
): boolean {
  if (!el) return false
  const { maxLines, tolerancePx = DEFAULT_TRUNCATION_TOLERANCE_PX } = options

  if (maxLines === 1) {
    return el.scrollWidth > el.clientWidth
  }

  // Measure the element's OWN BOX, lifting the clamp between the two reads.
  //
  // Two things are wrong with the obvious approaches, and this branch shipped
  // the second one before measuring it in a browser:
  //
  //   `scrollHeight > clientHeight` (dev) gives FALSE POSITIVES. scrollHeight
  //   includes glyph overflow, so a face whose ascent+descent exceed the CSS
  //   line box reports far more than the text occupies -- Sumana at 24px
  //   returns 49 for a 32.4px single line, and the element looks truncated
  //   while showing every word.
  //
  //   Comparing scrollHeight before and after lifting the clamp gives FALSE
  //   NEGATIVES, always. Measured in Chromium 148: scrollHeight ALREADY
  //   reports the full un-clamped content height while -webkit-line-clamp is
  //   applied, so lifting changes nothing and the difference is structurally
  //   zero. That silently switched this feature off everywhere.
  //
  // What does change on lift is the element's own height: clientHeight 41 ->
  // 79, and the border box 35.7px -> 52.5px. So read the border box, which is
  // what the reader actually sees, and ask whether removing the clamp would
  // make it taller. Glyph overflow sits outside that box, so it cannot inflate
  // either reading -- the same correction FitText needed.
  //
  // The flex sizing is lifted alongside them, because the clamp is not always the
  // binding constraint. A shrinkable flex item inside a fixed-height column is
  // sized by the flex algorithm, not by its content: when the column is
  // oversubscribed, `flex-shrink` pulls the box BELOW the `max-height` the clamp
  // wrote, and the text is then cut by `overflow: hidden` at whatever line the
  // box now ends on -- with no ellipsis, because `-webkit-line-clamp: N` only
  // ellipsizes past line N, and line N is still inside the content. Lifting only
  // the clamp and the max-height leaves the flex constraint in force, so both
  // reads return the same squashed height and this answers "not truncated" about
  // text the reader cannot see. `0 / 0 / auto` -- no grow, no shrink, content
  // basis -- is what makes the element size to its content, which is the height
  // the comparison is asking about.
  //
  // The three longhands are saved and restored rather than the `flex` shorthand:
  // a call site that set only `flexShrink` inline (the evidence row and the theme
  // title next to this one both do) reads back an empty shorthand, so writing the
  // shorthand and clearing it would silently drop that longhand. Each longhand
  // round-trips its own value, including the empty string for one that was never
  // set inline.
  //
  // The width is held at the laid-out width while lifted (plots plan LB). In a
  // ROW flex container the same `0 / 0 / auto` lift sizes the item to its
  // max-content width, so the text re-lays out on one line and reads SHORTER
  // than the clamp: every multi-line label beside an (i) or a badge answered
  // "not truncated" in Chromium while jsdom's stub said otherwise. In a column
  // container the width does not move, so holding it changes nothing there.
  //
  // The held width is the computed (used) width, never the bounding rect's:
  // the rect is measured after transforms, and the deck viewer scales slides
  // (`transform: scale(...)`, under 1 on a laptop, above 1 in fullscreen), so
  // a rect width would re-lay the text out narrower or wider than it is drawn.
  // The computed width is the layout width in the element's own box-sizing, so
  // writing it back inline reproduces the same box. Heights are compared as
  // two rect reads under the same transform, so their ratio is unaffected.
  const clamp = el.style.webkitLineClamp
  const maxHeight = el.style.maxHeight
  const flexGrow = el.style.flexGrow
  const flexShrink = el.style.flexShrink
  const flexBasis = el.style.flexBasis
  const width = el.style.width
  const layoutWidth = window.getComputedStyle(el).width
  const clampedHeight = el.getBoundingClientRect().height
  let naturalHeight = clampedHeight
  try {
    if (layoutWidth && layoutWidth.endsWith('px') && parseFloat(layoutWidth) > 0) {
      el.style.width = layoutWidth
    }
    el.style.webkitLineClamp = 'unset'
    el.style.maxHeight = 'none'
    el.style.flexGrow = '0'
    el.style.flexShrink = '0'
    el.style.flexBasis = 'auto'
    naturalHeight = el.getBoundingClientRect().height
  } finally {
    el.style.webkitLineClamp = clamp
    el.style.maxHeight = maxHeight
    el.style.flexGrow = flexGrow
    el.style.flexShrink = flexShrink
    el.style.flexBasis = flexBasis
    el.style.width = width
  }

  return naturalHeight > clampedHeight + tolerancePx
}

/**
 * Whole-line budget from an allotted pixel height. `minLines` / `maxLines` are the
 * schema floor and ceiling; the result is never outside that range.
 *
 * Zero or non-finite `allottedPx` returns `maxLines` so a failed measure (jsdom's
 * 0×0 box) does not shrink the declared clamp. A tiny but positive allotment still
 * floors to `minLines`.
 */
export function fitLineCount(
  allottedPx: number,
  lineHeightPx: number,
  minLines: number,
  maxLines: number,
): number {
  const lo = Number.isFinite(minLines) ? minLines : 1
  const hi = Number.isFinite(maxLines) ? Math.max(lo, maxLines) : lo
  if (!Number.isFinite(lineHeightPx) || lineHeightPx <= 0) {
    return hi
  }
  if (!Number.isFinite(allottedPx) || allottedPx <= 0) {
    return hi
  }
  return Math.min(hi, Math.max(lo, Math.floor(allottedPx / lineHeightPx)))
}

/**
 * The unitless CSS var (`var(--ds-lh-relaxed)` = 1.4) computes to px in a browser.
 * jsdom (and `line-height: normal`) do not; fall back to `fontSize * 1.4` or a
 * unitless multiplier times font size.
 */
function parseComputedLineHeightPx(computed: {
  lineHeight: string
  fontSize: string
}): number {
  const fontSizePx = parseFloat(computed.fontSize)
  const fallbackFs = Number.isFinite(fontSizePx) && fontSizePx > 0 ? fontSizePx : 16
  const raw = (computed.lineHeight || '').trim()
  if (raw.endsWith('px')) {
    const px = parseFloat(raw)
    if (Number.isFinite(px) && px > 0) {
      return px
    }
  }
  if (raw === 'normal' || raw === '') {
    return fallbackFs * 1.4
  }
  if (isUnitlessLineHeightValue(raw)) {
    const numeric = parseFloat(raw)
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric * fallbackFs
    }
  }
  return fallbackFs * 1.4
}

function parsePxLength(value: string): number {
  const raw = (value || '').trim()
  if (!raw.endsWith('px')) {
    return 0
  }
  const px = parseFloat(raw)
  return Number.isFinite(px) && px > 0 ? px : 0
}

/**
 * Lift clamp + flex longhands, fill remaining flex space, read the ALLOTTED
 * height, restore. Filling (`1 1 0%`) is the point: a content-sized lift would
 * report natural height, and a squashed box would never regain lines when the
 * parent grows (the fixed point `fitLines` exists to avoid).
 *
 * The three longhands are saved and restored rather than the `flex` shorthand:
 * a call site that set only `flexShrink` inline reads back an empty shorthand,
 * so writing the shorthand and clearing it would silently drop that longhand.
 */
function readFlexAllottedHeight(el: HTMLElement): number {
  const clamp = el.style.webkitLineClamp
  const maxHeight = el.style.maxHeight
  const flexGrow = el.style.flexGrow
  const flexShrink = el.style.flexShrink
  const flexBasis = el.style.flexBasis
  el.style.webkitLineClamp = 'unset'
  el.style.maxHeight = 'none'
  el.style.flexGrow = '1'
  el.style.flexShrink = '1'
  el.style.flexBasis = '0%'
  const allotted = el.getBoundingClientRect().height
  el.style.webkitLineClamp = clamp
  el.style.maxHeight = maxHeight
  el.style.flexGrow = flexGrow
  el.style.flexShrink = flexShrink
  el.style.flexBasis = flexBasis
  return allotted
}

/**
 * D17: `tooltipOnTruncate` deliberately drops the native `title` so the Radix tooltip is
 * not doubled by a slower browser one. In a static export there is no Radix tooltip to
 * double, so the native attribute comes back: without it the clamped text has no carrier
 * at all. An explicit `title` still wins over the clamped content, and a non-string child
 * (a fragment, an element) has no native representation, so it contributes none.
 */
function resolveNativeTitle(params: {
  title?: string
  children: React.ReactNode
  tooltipOnTruncate: boolean
  isStaticRender: boolean
}): string | undefined {
  const { title, children, tooltipOnTruncate, isStaticRender } = params
  if (!tooltipOnTruncate) {
    return title
  }
  if (!isStaticRender) {
    return undefined
  }
  if (title) {
    return title
  }
  if (typeof children === 'string') {
    return children
  }
  return undefined
}

export function TextClamp({
  children,
  minLines,
  maxLines,
  lineHeight,
  descenderBuffer = '0.15em',
  className,
  style,
  as = 'div',
  innerRef,
  id,
  title,
  'data-testid': dataTestId,
  tooltipOnTruncate = false,
  renderMode: renderModeProp,
  fitLines = false,
  onTruncatedChange,
}: TextClampProps) {
  const pageRenderMode = useContext(TextRenderModeContext)
  const renderMode = renderModeProp ?? pageRenderMode ?? 'interactive'
  const isStaticRender = renderMode === 'static'
  // Wave D (owner rule: complete text ships, cut text fails): a static render, the
  // PDF and screenshot pass, never clamps. Text llmfill accepted over its box renders
  // whole and wraps; the overflow is the typed warning the pipeline already recorded
  // (`text_over_box`, `slide_content_overflow`). An interactive view keeps its clamp
  // and shows the whole text on hover (`tooltipOnTruncate`, else the native title).
  const clampsText = !isStaticRender
  // D17: one gate for every tooltip-only code path below — the measurement effect, the
  // native-title suppression and the Radix wrapper. A static render never measures either,
  // which also spares the headless pass the forced layout that detection costs.
  const isTooltipEnabled = tooltipOnTruncate && !isStaticRender
  const isDetectionEnabled = isTooltipEnabled || (onTruncatedChange != null && !isStaticRender)
  const Tag = as
  const Element = Tag as any
  const lh = calcLineHeightValue(lineHeight)

  const measureRef = useRef<HTMLElement | null>(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [fittedLines, setFittedLines] = useState<number | null>(null)
  const [fittedMaxHeightPx, setFittedMaxHeightPx] = useState<number | null>(null)

  const appliedLines = !clampsText
    ? undefined
    : fitLines && fittedLines != null ? fittedLines : maxLines
  const lineClampStyle: React.CSSProperties =
    appliedLines === 1
      ? {
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }
      : appliedLines
        ? {
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: appliedLines,
            overflow: 'hidden',
          }
        : {}
  const descenderBufferValue =
    typeof descenderBuffer === 'number' ? `${descenderBuffer}em` : descenderBuffer
  const minHeight =
    minLines != null ? buildClampedHeight(lh, minLines) : undefined
  const maxHeight = !clampsText
    ? undefined
    : fitLines && fittedMaxHeightPx != null
      ? `${fittedMaxHeightPx}px`
      : maxLines != null
        ? buildClampedHeight(lh, maxLines, descenderBufferValue)
        : undefined

  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      measureRef.current = node
      if (typeof innerRef === 'function') {
        innerRef(node)
      } else if (innerRef) {
        ;(innerRef as React.MutableRefObject<HTMLElement | null>).current = node
      }
    },
    [innerRef],
  )

  // D4: measure on mount/update, and on resize if a ResizeObserver is available (jsdom has
  // none, so this degrades to a single measurement in tests, which is what stubbed
  // scroll-metric tests exercise). useLayoutEffect keeps the truncated state settled before
  // paint (and before RTL's render() returns), avoiding an extra async wait in tests.
  useLayoutEffect(() => {
    if (!isDetectionEnabled) {
      setIsTruncated(false)
      return
    }
    const el = measureRef.current
    if (!el) return

    // Read the CURRENT node every time, never the one captured when the effect
    // ran. Detecting truncation changes the rendered tree -- the element gets
    // wrapped in TooltipProvider/Tooltip/TooltipTrigger -- so React unmounts
    // this node and mounts a fresh one. Chromium then fires the observer for
    // the detached node at 0x0, and measuring THAT returned "not truncated",
    // which unwrapped the tooltip again. The feature looked fixed and was still
    // dead in a browser; jsdom has no ResizeObserver, so no test could see it.
    const measure = () => {
      const node = measureRef.current
      if (node) setIsTruncated(isTextClampTruncated(node, { maxLines }))
    }
    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
    // `isTruncated` is a dependency so the effect re-runs after that remount and
    // observes the node that is actually on screen. It settles: the second run
    // measures the live node, gets the same answer, and setState with an equal
    // value does not re-render.
  }, [isDetectionEnabled, maxLines, children, isTruncated, fittedLines])

  useLayoutEffect(() => {
    if (onTruncatedChange && !isStaticRender) {
      onTruncatedChange(isTruncated)
    }
  }, [onTruncatedChange, isStaticRender, isTruncated])

  // D3: own effect, not gated by isTooltipEnabled / static. Lifts clamp + flex
  // longhands, fills remaining space, reads allotted height, restores, then
  // quantizes to a whole line count. Re-runs on ResizeObserver (element AND
  // parent), document.fonts.ready, and one rAF after each re-fit so the static
  // render's assetsReady gate sees the final layout.
  useLayoutEffect(() => {
    if (!clampsText || !fitLines || maxLines == null || maxLines === 1) {
      return
    }
    const el = measureRef.current
    if (!el) return

    const lineFloor = minLines ?? 1
    const lineCeiling = maxLines
    let cancelled = false
    let rafId = 0

    function applyFit() {
      const node = measureRef.current
      if (!node || cancelled) {
        return
      }
      const allottedPx = readFlexAllottedHeight(node)
      if (!Number.isFinite(allottedPx) || allottedPx <= 0) {
        return
      }
      const computed = window.getComputedStyle(node)
      const lineHeightPx = parseComputedLineHeightPx(computed)
      if (!Number.isFinite(lineHeightPx) || lineHeightPx <= 0) {
        return
      }
      const lines = fitLineCount(allottedPx, lineHeightPx, lineFloor, lineCeiling)
      const nextMaxHeightPx = lines * lineHeightPx + parsePxLength(computed.paddingBottom)
      setFittedLines((prev) => (prev === lines ? prev : lines))
      setFittedMaxHeightPx((prev) => (prev === nextMaxHeightPx ? prev : nextMaxHeightPx))
    }

    function scheduleFit() {
      applyFit()
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(() => {
        if (!cancelled) {
          applyFit()
        }
      })
    }

    scheduleFit()

    const fonts = (document as Document & { fonts?: { ready?: Promise<unknown> } }).fonts
    if (fonts && typeof fonts.ready?.then === 'function') {
      fonts.ready
        .then(() => {
          if (!cancelled) {
            scheduleFit()
          }
        })
        .catch(() => {})
    }

    if (typeof ResizeObserver === 'undefined') {
      return () => {
        cancelled = true
        if (rafId) {
          cancelAnimationFrame(rafId)
        }
      }
    }

    const observer = new ResizeObserver(() => {
      scheduleFit()
    })
    const live = measureRef.current
    if (live) {
      observer.observe(live)
      if (live.parentElement) {
        observer.observe(live.parentElement)
      }
    }

    return () => {
      cancelled = true
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      observer.disconnect()
    }
  }, [clampsText, fitLines, minLines, maxLines, children, lineHeight, isTruncated])

  const textElement = (
    <Element
      ref={setRefs}
      id={id}
      title={resolveNativeTitle({
        title,
        children,
        tooltipOnTruncate,
        isStaticRender,
      })}
      data-testid={dataTestId}
      {...(fitLines ? { 'data-fit-lines': 'true' } : {})}
      className={className}
      style={{
        lineHeight: lh,
        minHeight,
        maxHeight,
        paddingBottom: descenderBufferValue,
        marginBottom: `calc(-1 * ${descenderBufferValue})`,
        ...lineClampStyle,
        ...style,
      }}
    >
      {children}
    </Element>
  )

  if (!isTooltipEnabled || !isTruncated) {
    return textElement
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>{textElement}</TooltipTrigger>
        <TooltipContent>{children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
